import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useActiveCompanyId } from '@/lib/active-company';
import { useCanEdit } from '@/lib/can-edit';
import { downloadWithCompanyHeader } from '@/lib/download';
import { useSnapshots } from '@/lib/snapshots';
import { ReportHeader } from './_shared/ReportHeader';
import { GenerateSnapshotDialog } from './_shared/GenerateSnapshotDialog';

export function SnapshotsList() {
  const companyId = useActiveCompanyId();
  const canEdit = useCanEdit();
  const snapshots = useSnapshots(companyId);

  if (!companyId) return <p className="p-10">Select a company first.</p>;

  return (
    <div className="mx-auto flex max-w-content-wide flex-col gap-gap-group px-5 py-gap-group md:px-10 lg:px-20">
      <ReportHeader mode="archive" />

      <section
        className="rounded-lg border border-border bg-surface"
        data-testid="snapshots-list"
      >
        {snapshots.isLoading ? (
          <p className="p-7 text-body-sm text-text-secondary">Loading…</p>
        ) : snapshots.data && snapshots.data.length > 0 ? (
          <ul className="divide-y divide-border">
            {snapshots.data.map((s, idx, all) => {
              // List is newest-first; the next entry is the prior snapshot.
              const previousId = all[idx + 1]?.id;
              return (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-4 px-7 py-4"
                >
                  <div>
                    <p className="font-medium text-text-primary">
                      {s.name ?? new Date(s.generatedAt).toLocaleString()}
                    </p>
                    <p className="text-body-sm text-text-secondary">
                      {s.name ? `${new Date(s.generatedAt).toLocaleString()} · ` : ''}
                      Generated by {s.generatedBy.name ?? s.generatedBy.email}
                      {s.compliancePct !== null
                        ? ` · ${s.compliancePct}% compliant`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/report/snapshots/${s.id}`}
                      className="text-body-sm font-medium text-accent hover:underline"
                    >
                      View
                    </Link>
                    {previousId ? (
                      <Link
                        to={`/report/compare?from=${previousId}&to=${s.id}`}
                        className="text-body-sm font-medium text-accent hover:underline"
                      >
                        Compare
                      </Link>
                    ) : null}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        downloadWithCompanyHeader(
                          `/api/Snapshots/${s.id}/export.pdf`,
                          `hexdi-snapshot-${s.id}.pdf`,
                        )
                      }
                    >
                      Download PDF
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-start gap-3 p-7">
            <p className="text-body text-text-secondary">
              No snapshots yet. Generate one to capture an auditable record of your
              current compliance posture.
            </p>
            {canEdit ? (
              <GenerateSnapshotDialog
                trigger={<Button variant="dark">Generate your first snapshot</Button>}
              />
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
